import React, { createContext, useContext, useState, useCallback } from 'react';

export interface SiteContextData {
  siteId: number | null;
  siteName: string | null;
  assetOwnerId: number | null;
  assetOwnerName: string | null;
  assetOwnerLogoUrl?: string | null;
}

interface SiteContextType {
  currentSite: SiteContextData;
  setSiteSelection: (siteId: number, siteName: string, assetOwnerId: number, assetOwnerName: string, assetOwnerLogoUrl?: string) => void;
  clearSiteSelection: () => void;
  isInSiteContext: boolean;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentSite, setCurrentSite] = useState<SiteContextData>({
    siteId: null,
    siteName: null,
    assetOwnerId: null,
    assetOwnerName: null,
    assetOwnerLogoUrl: null,
  });

  const setSiteSelection = useCallback((siteId: number, siteName: string, assetOwnerId: number, assetOwnerName: string, assetOwnerLogoUrl?: string) => {
    setCurrentSite({
      siteId,
      siteName,
      assetOwnerId,
      assetOwnerName,
      assetOwnerLogoUrl,
    });
  }, []);

  const clearSiteSelection = useCallback(() => {
    setCurrentSite({
      siteId: null,
      siteName: null,
      assetOwnerId: null,
      assetOwnerName: null,
      assetOwnerLogoUrl: null,
    });
  }, []);

  const isInSiteContext = currentSite.siteId !== null;

  return (
    <SiteContext.Provider value={{
      currentSite,
      setSiteSelection,
      clearSiteSelection,
      isInSiteContext,
    }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSiteContext = () => {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSiteContext must be used within a SiteProvider');
  }
  return context;
}; 